<template>
  <div class="flex space-x-2 divide-x bg-white px-5">
    <div class="h-90 space-y-3 overflow-y-auto py-5 pr-5">
      <div v-for="(title, type) in DATE_PICKER_TITLES" :key="type">
        <button
          class="w-40 rounded-sm border p-3 text-left font-medium"
          :class="
            selectedDatePickerType === type
              ? 'selected-date-picker-button'
              : 'date-picker-button'
          "
          @click="selectedDatePickerType = type as DatePickerTypes"
        >
          {{ title }}
        </button>
        <div
          v-if="
            type === DatePickerTypes.RANGE &&
            selectedDatePickerType === DatePickerTypes.RANGE &&
            range
          "
          class="mt-3 flex flex-col space-y-1.5"
        >
          <button
            v-for="button in SHORTCUT_BUTTONS"
            :key="button.id"
            class="w-fit-content rounded-3xl border px-2 py-0.5 text-xs"
            :class="
              selectedShortcutButton === button.id
                ? 'bg-blue-600 text-white'
                : 'text-gray-500'
            "
            @click="button.handler"
          >
            {{ button.label }}
          </button>
        </div>
      </div>
    </div>
    <div class="flex flex-col items-center py-5 pl-5">
      <dynamic-date-picker
        v-if="
          dynamicDateOptions?.length &&
          selectedDatePickerType === DatePickerTypes.DYNAMIC
        "
        v-model="dynamicDate"
        :dynamic-date-options="dynamicDateOptions"
      />
      <lf-calendar
        v-else
        v-model="dateRange"
        :multi-calendar="multiCalendar"
        :month-picker="selectedDatePickerType === DatePickerTypes.MONTH"
        :range
      />
      <lf-button
        v-if="showSelectButtons"
        class="mt-5 h-10 w-1/2 rounded-sm bg-blue-600 text-white"
        centered
        :disabled="!selectedDateArray.length"
        @click="submit"
      >
        {{ $t("COMMON.SELECT_PERIOD") }}
      </lf-button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { type PropType, computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { endOfMonth, isValid, startOfMonth } from "date-fns";

import { DynamicDate } from "@/enums/datetime";
import { getObjectKeys } from "@/helpers/common";
import { formatDateCustom } from "@/helpers/formatting";
import { getShortcutDate } from "@/helpers/formatting";

import LfButton from "@/components/ui/buttons/LfButton.vue";
import DynamicDatePicker from "@/components/ui/inputs/DatePicker/DynamicDatePicker.vue";
import LfCalendar from "@/components/ui/inputs/DatePicker/LfCalendar.vue";

import type { DynamicDateOption } from "@/models/datePicker";

export interface MonthYearDate {
  month: number;
  year: number;
}

const props = defineProps({
  modelValue: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  dateFormat: {
    type: String,
    default: "MM/dd/yyyy",
  },
  showSelectButtons: {
    type: Boolean,
    default: true,
  },
  multiCalendar: {
    type: Boolean,
    default: false,
  },
  dynamicDateOptions: {
    type: Array as PropType<DynamicDateOption[]>,
    default: () => [],
  },
  showMonthTab: {
    type: Boolean,
    default: true,
  },
  range: {
    type: Boolean,
    default: true,
  },
});

enum DatePickerTypes {
  RANGE = "range",
  MONTH = "month",
  DYNAMIC = "dynamic",
}

const emit = defineEmits<{
  "update:modelValue": [dynamicDate: string[]];
  close: [];
}>();

const { t } = useI18n();
const selectedShortcutButton = ref("");

const DATE_PICKER_TITLES = computed(() => {
  const titles: Record<string, string> = {
    [DatePickerTypes.RANGE]: props.range
      ? t("COMMON.DATES.DATEPICKER_TYPE.RANGE")
      : t("COMMON.DATES.DATEPICKER_TYPE.CUSTOM"),
  };
  if (props.showMonthTab) {
    titles[DatePickerTypes.MONTH] = t("COMMON.DATES.DATEPICKER_TYPE.MONTH");
  }
  if (props.dynamicDateOptions.length) {
    titles[DatePickerTypes.DYNAMIC] = t("COMMON.DATES.DATEPICKER_TYPE.DYNAMIC");
  }
  return titles;
});

const SHORTCUT_BUTTONS = getObjectKeys(DynamicDate).map((key) => ({
  id: DynamicDate[key],
  label: t(`COMMON.DATES.${key}`),
  handler: () => handleShortcutClick(DynamicDate[key]),
}));

const selectedDatePickerType = ref(
  props.dynamicDateOptions.length
    ? DatePickerTypes.DYNAMIC
    : DatePickerTypes.RANGE
);

type DateValue = string | Date | MonthYearDate;
type DateSelection = DateValue | DateValue[] | null;

const selectedDate = ref<DateValue[]>(props.modelValue);

// Helper to normalize value to array (VueDatePicker returns single value when range=false)
const normalizeToArray = (value: DateSelection): DateValue[] => {
  if (value === null || value === undefined) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
};

const selectedDateArray = computed(() => normalizeToArray(selectedDate.value));

const dynamicDate = computed<string | undefined>({
  get() {
    return (selectedDateArray.value as string[]).filter(
      (dateItem) =>
        props.dynamicDateOptions.findIndex(
          (option) => option.value === dateItem
        ) !== -1
    )[0];
  },
  set(dynamicDate: string | undefined) {
    if (!dynamicDate) return;
    selectedDate.value = [dynamicDate];
    if (!props.showSelectButtons) {
      submit();
    }
  },
});

const dateRange = computed({
  get() {
    return (selectedDateArray.value as string[]).filter(
      (dateItem) =>
        props.dynamicDateOptions.findIndex(
          (option) => option.value === dateItem
        ) === -1
    );
  },
  set(value) {
    selectedDate.value = normalizeToArray(value);
    if (!props.showSelectButtons) {
      submit();
    }
  },
});

const handleShortcutClick = (shortcut: string) => {
  selectedShortcutButton.value = shortcut;
  selectedDate.value = getShortcutDate(shortcut);
  if (!props.showSelectButtons) {
    submit();
  }
};

const submit = () => {
  emit(
    "update:modelValue",
    selectedDateArray.value.map((date, index) => {
      if (isValid(date)) {
        return formatDateCustom(date as Date, props.dateFormat, true);
      } else if (
        typeof date === "object" &&
        "month" in date &&
        "year" in date
      ) {
        const tempDate = new Date(date.year, date.month);
        return formatDateCustom(
          index === 0 ? startOfMonth(tempDate) : endOfMonth(tempDate),
          props.dateFormat,
          true
        );
      } else {
        return date as string;
      }
    })
  );
  if (props.showSelectButtons) {
    emit("close");
  }
};

watch(
  () => props.modelValue,
  (newVal: string[]) => {
    selectedDate.value = newVal;
  }
);
</script>
<style>
@reference "@/assets/tailwind.css";

.selected-date-picker-button {
  @apply border-transparent bg-primary/10 text-primary;
}
.date-picker-button {
  @apply text-label;
}
</style>
